import React, { useState, useEffect, FC } from "react";
import {
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  Modal,
  ModalBody,
  UncontrolledDropdown,
  Badge,
  Col,
  Row,
  UncontrolledTooltip,
} from "reactstrap";
import { Icon, Button, TooltipComponent } from "../../../components/Component";
import Swal from "sweetalert2";
import toolMasterApis from "../../../api/master/toolMaster";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import TransactionFlowDefaultTemplate from "../../others/transactionFlowDefaultTemplate";
import dayjs from "dayjs";
import ToolMasterForm from "./ToolMasterForm";
import FormatDate from "../DateFormater";
import { normalizeStatus } from "../../../utils/Utils";

const ToolMaster: FC = () => {
  const [refreshToggle, setRefreshToggle] = useState(false);
  const { data: accessibility } = useSelector(
    (state: any) => state.accessibility
  );
  const [allowedActions, setAllowedActions] = useState<any>({
    add: 0,
    view: 0,
    edit: 0,
    approve: 0,
    reject: 0,
    cancel: 0,
  });

  // Modal states
  const [isOpen, setIsOpen] = useState(false);
  const [viewModal, setViewModal] = useState(false);
  const [detail, setDetail] = useState<any>({});

  const [popUpContent, setPopUpContent] = useState<JSX.Element | null>(null);

  useEffect(() => {
    if (accessibility) {
      try {
        const { add, view, edit, approve, reject, cancel } =
          accessibility["Master"];
        setAllowedActions({ add, view, edit, approve, reject, cancel });
      } catch (error) {
        console.log(error);
      }
    }
  }, [accessibility]);

  const navigate = useNavigate();

  const handleStatusChange = async (
    toolId: any,
    action: "activate" | "inactivate"
  ) => {
    const actionText = action === "activate" ? "activate" : "inactivate";
    const status = action === "activate" ? "Active" : "InActive";

    Swal.fire({
      title: `Are you sure?`,
      text: `You want to ${actionText} this tool & machinery!`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: `Yes, ${actionText} it!`,
      allowOutsideClick: false,
      buttonsStyling: false,
      customClass: {
        confirmButton: "btn btn-primary",
        cancelButton: "btn btn-secondary",
      },
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const response =
            action === "activate"
              ? await toolMasterApis.allowActiveStatus(toolId)
              : await toolMasterApis.removeActiveStatus(toolId);

          if (response.status) {
            await Swal.fire(
              "Success!",
              `Tools & Machinery has been ${
                action === "activate" ? "Activated" : "Inactivated"
              }.`,
              "success"
            );
            refreshData();
          } else {
            await Swal.fire("Error", response.message, "error");
          }
        } catch (error: any) {
          await Swal.fire("Error", error.message, "error");
        }
      }
    });
  };

  const handleDelete = (toolId: any) => {
    Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, delete it!",
      allowOutsideClick: false,
      buttonsStyling: false,
      customClass: {
        confirmButton: "btn btn-primary",
        cancelButton: "btn btn-secondary",
      },
    }).then(async (result) => {
      if (result.isConfirmed) {
        await deleteToolById(toolId);
      }
    });
  };

  const deleteToolById = async (id: any) => {
    try {
      const { status, message } = await toolMasterApis.deleteToolyId(id);
      if (status) {
        await Swal.fire("Deleted!", message, "success");
        refreshData();
      } else {
        await Swal.fire("Error", message, "error");
      }
    } catch (error: any) {
      await Swal.fire("Error", error.message, "error");
    }
  };

  const refreshData = () => {
    setRefreshToggle((prev) => !prev);
  };

  const loadDetail = async (toolId: any) => {
    try {
      const response = await toolMasterApis.fetchToolById(toolId);
      if (response && response.status) {
        setDetail(response.data);
      } else {
        console.error("Failed to fetch tool details");
        setDetail({ id: toolId });
      }
    } catch (error) {
      console.error("Error fetching tool details:", error);
      setDetail({ id: toolId });
    }
  };

  const handleModelPopUp = (id?: any, type?: string) => {
    const content = (
      <ToolMasterForm
        formAction={type}
        id={id}
        toggleModelPopUp={() => {
          setIsOpen(false);
        }}
        refreshData={refreshData}
      />
    );
    setPopUpContent(content);
    setIsOpen(true);
  };

  // Define select filters for the template
  const selectFilters = [
    {
      title: "Status",
      placeholder: "Select Status",
      options: [
        { label: "Active", value: "Active" },
        { label: "Inactive", value: "Inactive" },
      ],
      onChange: (value: any) => {
        console.log("Status selected:", value);
      },
    },
  ];

  const columns = [
    {
      title: "ID",
      dataIndex: "sequenceId",
      key: "sequenceId",
      width: 120,
    },
    {
      title: "Type",
      dataIndex: "typeName",
      key: "typeName",
      width: 200,
      render: (_: any, record: any) => {
        if (record.typeName === "Others" && record.customType) {
          return `${record.typeName} (${record.customType})`;
        }
        return record.typeName;
      },
    },
    {
      title: "Name",
      dataIndex: "toolName",
      key: "toolName",
      width: 150,
    },
    {
      title: "Description",
      dataIndex: "description",
      key: "description",
      width: 200,
      render: (text: string, _: any, index: number) => {
        if (!text) return "N/A";

        return (
          <div className="d-flex align-items-center">
            <span id={`desc-tooltip-${index}`}>
              {text.length > 25 ? `${text.substring(0, 25)}...` : text}
            </span>

            {text.length > 25 && (
              <TooltipComponent
                iconClass="info-circle text-info ms-1"
                icon="info"
                id={`desc-tooltip-${index}`}
                direction="top"
                text={text}
                containerClassName=""
              />
            )}
          </div>
        );
      },
    },
    {
      title: "Created Date",
      dataIndex: "createdDate",
      key: "createdDate",
      width: 150,
      render: (text: any) => <span>{dayjs(text).format("DD-MMM-YYYY")}</span>,
    },
    {
      title: "Created By",
      dataIndex: "createdByUserName",
      key: "createdByUserName",
      width: 150,
      render: (text: any) => text || "N/A",
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 100,
      render: (text: any) => {
        const normalizedStatus = normalizeStatus(text);
        return (
          <span
            className={`tb-status text-${
              normalizedStatus === "Active" ? "success" : "danger"
            }`}
          >
            {normalizedStatus}
          </span>
        );
      },
    },
    {
      title: "Details",
      dataIndex: "action",
      key: "action",
      width: 100,
      fixed: "right",
      render: (text: any, record: any) => {
        const normalizedStatus = normalizeStatus(record.status);
        const canView = allowedActions?.view;
        const canEdit =
          allowedActions?.edit && normalizeStatus(record.status) === "Active";
        const canToggleStatus = true;
        const canDelete = allowedActions?.cancel;
        const isActive = normalizedStatus === "Active";

        const availableActions = [
          canView,
          canEdit,
          canToggleStatus,
          canDelete,
        ].filter(Boolean).length;

        return (
          <div
            className="details-cell"
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              minHeight: "40px",
              gap: "8px",
            }}
          >
            {canEdit && (
              <div className="edit-hover-wrapper">
                <a
                  href="#edit"
                  className="btn btn-icon btn-trigger edit-hover-icon"
                  id={`edit-${record.id}`}
                  onClick={(ev) => {
                    ev.preventDefault();
                    handleModelPopUp(record.id, "edit");
                  }}
                >
                  <Icon name="edit-alt-fill"></Icon>
                </a>

                <TooltipComponent
                  id={`edit-tooltip-${record.id}`}
                  direction="top"
                  icon=""
                  text="Edit"
                  containerClassName=""
                  target={`edit-${record.id}`}
                  iconClass={undefined}
                />
              </div>
            )}

            {(canView || canEdit || canToggleStatus || canDelete) && (
              <UncontrolledDropdown direction="start">
                <DropdownToggle
                  tag="button"
                  className="btn btn-sm btn-icon btn-light"
                  style={{
                    border: "none",
                    background: "none",
                    position: "relative",
                    zIndex: 1,
                    color: "#526484",
                  }}
                >
                  <Icon name="more-h" style={{ color: "inherit" }}></Icon>
                </DropdownToggle>
                <DropdownMenu
                  end
                  style={{
                    position: "fixed",
                    minWidth: "160px",
                    boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                    border: "1px solid #e5e5e5",
                    zIndex: 9999,
                    inset: "auto auto auto auto !important",
                  }}
                  container="body"
                >
                  <ul className="link-list-opt no-bdr" style={{ margin: 0 }}>
                    {/* view option */}
                    {canView && (
                      <li>
                        <DropdownItem
                          tag="a"
                          href="#view"
                          onClick={async (ev) => {
                            ev.preventDefault();
                            await loadDetail(record.id);
                            setViewModal(true);
                          }}
                          style={{
                            padding: "8px 16px",
                            display: "flex",
                            alignItems: "center",
                          }}
                        >
                          <Icon name="focus" className="me-2" />
                          <span>View Details</span>
                        </DropdownItem>
                      </li>
                    )}

                    {/* Edit option - only for active records */}
                    {canEdit && (
                      <li>
                        <DropdownItem
                          tag="a"
                          href="#edit"
                          onClick={(ev) => {
                            ev.preventDefault();
                            handleModelPopUp(record.id, "edit");
                          }}
                          style={{
                            padding: "8px 16px",
                            display: "flex",
                            alignItems: "center",
                            textDecoration: "none",
                          }}
                        >
                          <Icon name="edit" className="me-2"></Icon>
                          <span>Edit</span>
                        </DropdownItem>
                      </li>
                    )}

                    {/* Status toggle - always show one of them */}
                    {canToggleStatus && (
                      <>
                        {isActive ? (
                          <li>
                            <DropdownItem
                              tag="a"
                              href="#inactivate"
                              onClick={(ev) => {
                                ev.preventDefault();
                                handleStatusChange(record.id, "inactivate");
                              }}
                              style={{
                                padding: "8px 16px",
                                display: "flex",
                                alignItems: "center",
                                textDecoration: "none",
                              }}
                            >
                              <Icon name="cross-circle" className="me-2"></Icon>
                              <span>Inactivate</span>
                            </DropdownItem>
                          </li>
                        ) : (
                          <li>
                            <DropdownItem
                              tag="a"
                              href="#activate"
                              onClick={(ev) => {
                                ev.preventDefault();
                                handleStatusChange(record.id, "activate");
                              }}
                              style={{
                                padding: "8px 16px",
                                display: "flex",
                                alignItems: "center",
                                textDecoration: "none",
                              }}
                            >
                              <Icon name="check-circle" className="me-2"></Icon>
                              <span>Activate</span>
                            </DropdownItem>
                          </li>
                        )}
                      </>
                    )}

                    {/* Delete option */}
                    {/* {canDelete && (
                          <li>
                            <DropdownItem
                              tag="a"
                              href="#delete"
                              onClick={(ev) => {
                                ev.preventDefault();
                                handleDelete(record.id);
                              }}
                              style={{
                                padding: "8px 16px",
                                display: "flex",
                                alignItems: "center",
                                textDecoration: "none",
                              }}
                            >
                              <Icon name="trash" className="me-2"></Icon>
                              <span>Delete</span>
                            </DropdownItem>
                          </li>
                        )} */}
                  </ul>
                </DropdownMenu>
              </UncontrolledDropdown>
            )}
          </div>
        );
      },
    },
  ];

  return (
    <>
      <TransactionFlowDefaultTemplate
        moduleKey="toolmaster-list"
        title="Tools & Machinery"
        columns={columns}
        selectFilters={selectFilters}
        extra={{
          tableScrollX: 1200,
          excelExport: true,
        }}
        refresh={refreshToggle}
        topRightJSX={
          allowedActions?.add ? (
            <Button
              color="primary"
              onClick={() => handleModelPopUp(null, "add")}
            >
              <Icon name="plus"></Icon>
              <span>Add</span>
            </Button>
          ) : (
            <></>
          )
        }
      />

      {/* Add/Edit Modal */}
      <Modal
        isOpen={isOpen}
        toggle={() => setIsOpen(false)}
        className="modal-dialog-centered"
        size="lg"
        backdrop="static"
      >
        <a
          href="#cancel"
          onClick={(ev) => {
            ev.preventDefault();
            setIsOpen(false);
          }}
          className="close"
        >
          <Icon name="cross-sm"></Icon>
        </a>
        <ModalBody>{popUpContent}</ModalBody>
      </Modal>

      {/* View Details Modal */}
      <Modal
        isOpen={viewModal}
        toggle={() => setViewModal(false)}
        className="modal-dialog-centered"
        size="lg"
        backdrop="static"
      >
        <ModalBody>
          <a
            href="#cancel"
            onClick={(ev) => {
              ev.preventDefault();
              setViewModal(false);
            }}
            className="close"
          >
            <Icon name="cross-sm"></Icon>
          </a>
          <div className="nk-modal-head">
            <h4 className="nk-modal-title title">Tools & Machinery Details</h4>
          </div>
          <div className="nk-tnx-details mt-sm-3">
            <Row className="gy-3">
              <Col lg={6}>
                <span className="sub-text">ID</span>
                <span className="caption-text">{detail.sequenceId}</span>
              </Col>
              <Col lg={6}>
                <span className="sub-text">Type</span>
                <span className="caption-text">
                  {detail.typeName === "Others" && detail.customType
                    ? `${detail.typeName} (${detail.customType})`
                    : detail.typeName}
                </span>
              </Col>
              <Col lg={6}>
                <span className="sub-text">Name</span>
                <span className="caption-text">{detail.toolName}</span>
              </Col>
              <Col lg={6}>
                <span className="sub-text">Description</span>
                <span className="caption-text">
                  {detail.description || "N/A"}
                </span>
              </Col>
              <Col lg={6}>
                <span className="sub-text">Status</span>
                <Badge
                  color={
                    normalizeStatus(detail.status) === "Active"
                      ? "success"
                      : "danger"
                  }
                  size="md"
                >
                  {normalizeStatus(detail.status)}
                </Badge>
              </Col>
              <Col lg={6}>
                <span className="sub-text">Created Date</span>
                <span className="caption-text">
                  {detail.createdDate ? FormatDate(detail.createdDate) : "N/A"}
                </span>
              </Col>
              <Col lg={6}>
                <span className="sub-text">Created By</span>
                <span className="caption-text">
                  {detail.createdByUserName || "N/A"}
                </span>
              </Col>
            </Row>
          </div>
        </ModalBody>
      </Modal>
    </>
  );
};

export default ToolMaster;
